import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { formatEur, formatDateShort, formatDuration, MISSION_STATUS_LABEL, MISSION_STATUS_COLOR } from '@/lib/format'

interface Mission {
  id: string
  title: string
  description: string
  type: string
  status: string
  date: string
  city: string
  postalCode: string
  duration: number
  amount: number
  notifiedCount: number
  _count: { assignments: number }
  assignments: Array<{
    status: string
    provider: { id: string; firstName: string; lastName: string; rating: number }
  }>
}

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'bg-accent/10 border-accent/25 text-accent',
  ASSIGNED: 'bg-success/10 border-success/25 text-success',
  IN_PROGRESS: 'bg-[#F59E0B]/10 border-[#F59E0B]/25 text-[#F59E0B]',
  COMPLETED: 'bg-white/[0.06] border-white/[0.10] text-text-muted',
  CANCELLED: 'bg-danger/10 border-danger/25 text-danger',
}

const STATUS_TABS = [
  { key: 'ALL', label: 'Toutes' },
  { key: 'OPEN', label: 'En attente' },
  { key: 'ASSIGNED', label: 'Assignées' },
  { key: 'COMPLETED', label: 'Terminées' },
  { key: 'CANCELLED', label: 'Annulées' },
]

export default function CFMesMissionsPage() {
  const [missions, setMissions] = useState<Mission[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('ALL')

  useEffect(() => {
    apiFetch<Mission[]>('/missions')
      .then((data) => setMissions(data.filter((m) => m.type === 'TRAINING')))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filtered = tab === 'ALL' ? missions : missions.filter((m) => m.status === tab)

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => <div key={i} className="h-20 glass-card rounded-xl animate-pulse" />)}
    </div>
  )

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Mes recherches de formateurs</h1>
        <p className="text-text-secondary text-sm mt-1">Missions publiées sur Velink pour trouver des formateurs indépendants.</p>
      </div>

      {/* Onglets statut */}
      <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.08] mb-6 w-fit flex-wrap">
        {STATUS_TABS.map(({ key, label }) => {
          const count = key === 'ALL' ? missions.length : missions.filter((m) => m.status === key).length
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === key ? 'bg-white/[0.10] text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
            >
              {label} ({count})
            </button>
          )
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-text-primary font-medium mb-1">
            {tab === 'ALL' ? 'Aucune mission publiée' : 'Aucune mission dans cette catégorie'}
          </p>
          <p className="text-text-secondary text-sm mb-5">
            {tab === 'ALL'
              ? 'Publiez une recherche de formateur depuis une demande de formation pour trouver des prestataires.'
              : ''}
          </p>
          {tab === 'ALL' && (
            <Link to="/cf-formations" className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl inline-block">
              Voir les demandes de formation →
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => {
            const accepted = m.assignments.filter((a) => a.status === 'ACCEPTED')
            const pending = m.assignments.filter((a) => a.status === 'PENDING')
            return (
              <Link
                key={m.id}
                to={`/missions/${m.id}`}
                className="glass-card p-5 block hover:bg-white/[0.04] transition-colors rounded-xl"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <p className="text-text-primary font-medium">{m.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_COLOR[m.status] ?? ''}`}>
                        {MISSION_STATUS_LABEL[m.status] ?? m.status}
                      </span>
                    </div>
                    <p className="text-text-muted text-xs">
                      {formatDateShort(m.date)} · {formatDuration(m.duration)} · {m.city} {m.postalCode}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <p className="text-text-primary font-semibold">{formatEur(m.amount)}</p>
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                      {m.notifiedCount > 0 && (
                        <span>🔔 {m.notifiedCount} notifié{m.notifiedCount > 1 ? 's' : ''}</span>
                      )}
                      {accepted.length > 0 ? (
                        <span className="text-success font-medium">✓ {accepted.length} accepté{accepted.length > 1 ? 's' : ''}</span>
                      ) : pending.length > 0 ? (
                        <span className="text-accent">{pending.length} candidature{pending.length > 1 ? 's' : ''}</span>
                      ) : (
                        <span>En attente de candidatures</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
